/** Etiquetas de valor en barras: helpers responsive y anti-solapamiento. */

export const BAR_LABEL_COMPACT_WIDTH = 480;
export const BAR_LABEL_MINIMAL_WIDTH = 360;
export const MIN_SEGMENT_LABEL_PX = 8;
export const MIN_SEGMENT_LABEL_FONT_PX = 8;
export const MIN_GROUPED_BAR_LABEL_PX = 12;

/** Baja la fuente si el segmento se achica (zoom); null si ya no cabe un dígito. */
export function fitBarSegmentLabelFontPx(
  segmentPx,
  preferredFontPx,
  minFontPx = MIN_SEGMENT_LABEL_FONT_PX
) {
  if (!(segmentPx > 0) || segmentPx < minFontPx) return null;
  const preferred = Number(preferredFontPx);
  const base = Number.isFinite(preferred) && preferred > 0 ? preferred : 12;
  return Math.min(base, Math.max(minFontPx, Math.floor(segmentPx) - 1));
}

/**
 * Tramo visible de un segmento recortado por el área del gráfico (zoom in).
 * Devuelve null si no queda nada útil en pantalla.
 */
export function visibleBarSegmentSpan(start, end, clipMin, clipMax) {
  const a = Math.min(start, end);
  const b = Math.max(start, end);
  const visStart = Math.max(a, clipMin);
  const visEnd = Math.min(b, clipMax);
  const size = visEnd - visStart;
  if (size < MIN_SEGMENT_LABEL_PX) return null;
  return { start: visStart, end: visEnd, size, center: (visStart + visEnd) / 2 };
}

export function isCompactBarLabelViewport(containerWidth) {
  return containerWidth > 0 && containerWidth < BAR_LABEL_COMPACT_WIDTH;
}

export function isMinimalBarLabelViewport(containerWidth) {
  return containerWidth > 0 && containerWidth < BAR_LABEL_MINIMAL_WIDTH;
}

export function barValueLabelFontPx(containerWidth) {
  if (isMinimalBarLabelViewport(containerWidth)) return 9;
  if (isCompactBarLabelViewport(containerWidth)) return 10;
  return 12;
}

export function resolveBarLabelMode({ showTotal, showSegment, containerWidth, categoryCount = 0 }) {
  if (!showTotal && !showSegment) {
    return { showTotal: false, showSegment: false };
  }
  if (isMinimalBarLabelViewport(containerWidth)) {
    return { showTotal: false, showSegment: false };
  }
  if (isCompactBarLabelViewport(containerWidth)) {
    return { showTotal: Boolean(showTotal), showSegment: false };
  }
  const crowded = categoryCount > 12 && showTotal && showSegment;
  return {
    showTotal: Boolean(showTotal),
    showSegment: crowded ? false : Boolean(showSegment),
  };
}

/** Separación mínima en px entre etiquetas vecinas (eje X o valores de línea). */
export function resolveMinHorizontalLabelGapPx(containerWidth, { kind = 'axis' } = {}) {
  const minimal = isMinimalBarLabelViewport(containerWidth);
  const compact = isCompactBarLabelViewport(containerWidth);
  if (kind === 'axis') {
    if (minimal) return 22;
    if (compact) return 20;
    return 16;
  }
  if (minimal) return 32;
  if (compact) return 26;
  return 20;
}

/**
 * Índices de categoría visibles a intervalos regulares según ancho útil del gráfico.
 * Siempre incluye el primero y el último.
 */
export function buildRegularLabelIndexSet(categoryCount, containerWidth, minPxGap) {
  if (categoryCount <= 0) return new Set();
  if (categoryCount === 1) return new Set([0]);

  const chartAreaWidth = Math.max(100, (containerWidth || 320) * 0.82);
  const slotWidth = chartAreaWidth / Math.max(categoryCount - 1, 1);

  if (slotWidth >= minPxGap) {
    return new Set(Array.from({ length: categoryCount }, (_, i) => i));
  }

  const stride = Math.max(1, Math.ceil(minPxGap / slotWidth));
  const indices = new Set();
  for (let i = 0; i < categoryCount; i += stride) {
    indices.add(i);
  }
  indices.add(categoryCount - 1);
  return indices;
}

/** Cuántas categorías del eje X pueden mostrarse sin amontonarse. @deprecated use buildRegularLabelIndexSet */
export function resolveCategoryLabelStride(containerWidth, categoryCount, { minPxPerLabel } = {}) {
  if (categoryCount <= 1) return 1;
  const minPx =
    minPxPerLabel ??
    resolveMinHorizontalLabelGapPx(containerWidth, { kind: 'axis' });
  const chartAreaWidth = Math.max(100, (containerWidth || 320) * 0.82);
  const maxVisible = Math.max(2, Math.floor(chartAreaWidth / minPx));
  if (categoryCount <= maxVisible) return 1;
  return Math.ceil(categoryCount / maxVisible);
}

/** Ticks del eje X (categorías) con muestreo por distancia horizontal regular. */
export function resolveCategoryAxisTickOptions(
  containerWidth,
  categoryCount = 0,
  { avgLabelChars = 11 } = {}
) {
  const minimal = isMinimalBarLabelViewport(containerWidth);
  const compact = isCompactBarLabelViewport(containerWidth);
  const fontSize = minimal ? 9 : compact ? 10 : 11;
  const estLabelWidth = Math.max(36, avgLabelChars * (fontSize * 0.58) + 6);

  const chartAreaWidth = Math.max(100, (containerWidth || 320) * 0.82);
  const slotWidth =
    categoryCount > 1 ? chartAreaWidth / (categoryCount - 1) : chartAreaWidth;

  // Horizontal solo si cabe el ancho estimado del texto; si no, rotar 45°.
  const horizontalFits = categoryCount <= 1 || slotWidth >= estLabelWidth * 0.9;
  const rotate = horizontalFits ? 0 : 45;
  const minPxGap = horizontalFits
    ? estLabelWidth * 0.9
    : resolveMinHorizontalLabelGapPx(containerWidth, { kind: 'axis' });

  const visibleIndices = buildRegularLabelIndexSet(categoryCount, containerWidth, minPxGap);
  const sampleLabels = categoryCount > 0 && visibleIndices.size < categoryCount;

  return {
    autoSkip: !sampleLabels,
    maxRotation: rotate,
    minRotation: rotate,
    font: { size: fontSize },
    visibleIndices: sampleLabels ? visibleIndices : null,
    rotated: rotate > 0,
  };
}

export function lineValueLabelFontPx(containerWidth) {
  if (isMinimalBarLabelViewport(containerWidth)) return 9;
  if (isCompactBarLabelViewport(containerWidth)) return 10;
  return 11;
}

/** Modo responsive para etiquetas de valor en gráficos de línea. */
export function resolveLineValueLabelMode({
  showValue,
  containerWidth,
  categoryCount = 0,
  datasetCount = 1,
}) {
  if (!showValue) {
    return { showValue: false, minPxGap: 0, fontPx: 11, skipZero: false };
  }

  const fontPx = lineValueLabelFontPx(containerWidth);
  let minPxGap = resolveMinHorizontalLabelGapPx(containerWidth, { kind: 'value' });
  if (datasetCount > 1) {
    minPxGap *= 1.1;
  }

  const crowded =
    categoryCount > 24 &&
    (isCompactBarLabelViewport(containerWidth) || isMinimalBarLabelViewport(containerWidth));

  return {
    showValue: true,
    minPxGap,
    fontPx,
    skipZero: crowded && categoryCount > 36,
  };
}

/** Decide índices visibles según posición X real en el canvas (intervalo horizontal regular). */
export function buildLineValueLabelIndexSet(metaPoints, categoryCount, minPxGap) {
  const indices = new Set();
  if (!metaPoints?.length || categoryCount <= 0) return indices;
  if (categoryCount === 1) {
    indices.add(0);
    return indices;
  }

  let lastX = -Infinity;
  metaPoints.forEach((element, index) => {
    if (!element || element.hidden) return;
    const isEdge = index === 0 || index === categoryCount - 1;
    if (isEdge || element.x - lastX >= minPxGap) {
      indices.add(index);
      lastX = element.x;
    }
  });
  indices.add(categoryCount - 1);
  return indices;
}

export function measureTextBox(ctx, text, fontPx) {
  const font = `${fontPx}px sans-serif`;
  ctx.font = font;
  const w = ctx.measureText(text).width;
  const h = Math.ceil(fontPx * 1.25);
  return { font, w, h };
}

export function boxesOverlap(a, b, pad = 2) {
  return !(
    a.x + a.w + pad <= b.x ||
    b.x + b.w + pad <= a.x ||
    a.y + a.h + pad <= b.y ||
    b.y + b.h + pad <= a.y
  );
}

/**
 * Dibuja texto si cabe sin solaparse con cajas ya colocadas.
 * align: 'center' | 'left', baseline: 'middle' | 'bottom'
 */
export function tryDrawLabel(ctx, text, x, y, { align = 'center', baseline = 'middle', fontPx = 12, placed = [] }) {
  const { font, w, h } = measureTextBox(ctx, text, fontPx);
  let boxX = x - w / 2;
  if (align === 'left') boxX = x;
  else if (align === 'right') boxX = x - w;

  let boxY = y - h / 2;
  if (baseline === 'bottom') boxY = y - h;
  else if (baseline === 'top') boxY = y;

  const box = { x: boxX - 2, y: boxY - 1, w: w + 4, h: h + 2 };
  if (placed.some((p) => boxesOverlap(box, p))) return false;

  placed.push(box);
  ctx.save();
  ctx.textAlign = align;
  ctx.textBaseline = baseline;
  ctx.fillStyle = '#333';
  ctx.font = font;
  ctx.fillText(text, x, y);
  ctx.restore();
  return true;
}
